(() => {
  'use strict';

  const config = window.ChromePods || {};
  const html = document.documentElement;
  const body = document.body;
  const liveRegion = document.querySelector('[data-live-region]');

  const announce = (message) => {
    if (!liveRegion) return;
    liveRegion.textContent = '';
    window.setTimeout(() => { liveRegion.textContent = message; }, 30);
  };

  const escapeHtml = (value = '') => String(value)
    .replaceAll('&', '&amp;')
    .replaceAll('<', '&lt;')
    .replaceAll('>', '&gt;')
    .replaceAll('"', '&quot;')
    .replaceAll("'", '&#039;');

  const formatMoney = (cents, currency = 'EUR') => {
    try {
      return new Intl.NumberFormat(config.locale || 'fr-FR', {
        style: 'currency',
        currency
      }).format(Number(cents || 0) / 100);
    } catch (_error) {
      return `${(Number(cents || 0) / 100).toFixed(2)} ${currency}`;
    }
  };

  // Soft entrance animations.
  if (!body.classList.contains('animations-disabled') && !window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          entry.target.classList.add('is-visible');
          observer.unobserve(entry.target);
        }
      });
    }, { threshold: 0.12 });
    document.querySelectorAll('.reveal').forEach((element) => observer.observe(element));
  } else {
    document.querySelectorAll('.reveal').forEach((element) => element.classList.add('is-visible'));
  }

  // Mobile menu.
  const mobileMenu = document.querySelector('[data-mobile-menu]');
  const mobileOverlay = document.querySelector('[data-mobile-menu-overlay]');
  const mobileOpen = document.querySelector('[data-mobile-menu-open]');
  const mobileClose = document.querySelector('[data-mobile-menu-close]');

  const setMobileMenu = (open) => {
    if (!mobileMenu || !mobileOverlay || !mobileOpen) return;
    mobileMenu.classList.toggle('is-open', open);
    mobileMenu.setAttribute('aria-hidden', String(!open));
    mobileOpen.setAttribute('aria-expanded', String(open));
    mobileOverlay.hidden = !open;
    body.classList.toggle('menu-open', open);
    if (open) mobileClose?.focus();
    else mobileOpen.focus();
  };

  mobileOpen?.addEventListener('click', () => setMobileMenu(true));
  mobileClose?.addEventListener('click', () => setMobileMenu(false));
  mobileOverlay?.addEventListener('click', () => setMobileMenu(false));

  // Product gallery.
  document.addEventListener('click', (event) => {
    const thumb = event.target.closest('[data-media-target]');
    if (!thumb) return;
    const gallery = thumb.closest('.product-gallery');
    if (!gallery) return;
    const mediaId = thumb.dataset.mediaTarget;
    gallery.querySelectorAll('.product-media').forEach((media) => media.classList.toggle('is-active', media.dataset.mediaId === mediaId));
    gallery.querySelectorAll('[data-media-target]').forEach((button) => button.classList.toggle('is-active', button === thumb));
  });

  // Product variants.
  const updateVariantUI = (select) => {
    const root = select.closest('[data-product-root]');
    if (!root) return;
    const json = root.querySelector('[data-product-variants]');
    if (!json) return;
    let variants = [];
    try { variants = JSON.parse(json.textContent); } catch (_error) { return; }
    const variant = variants.find((item) => String(item.id) === String(select.value));
    if (!variant) return;

    const price = root.querySelector('[data-product-price]');
    if (price) {
      const currency = config.currency || 'EUR';
      price.innerHTML = variant.compare_at_price && variant.compare_at_price > variant.price
        ? `<span class="price__sale">${escapeHtml(formatMoney(variant.price, currency))}</span><s class="price__compare">${escapeHtml(formatMoney(variant.compare_at_price, currency))}</s>`
        : `<span class="price__regular">${escapeHtml(formatMoney(variant.price, currency))}</span>`;
    }

    const addButton = root.querySelector('[data-add-to-cart]');
    const addText = root.querySelector('[data-add-to-cart-text]');
    const quantityInput = root.querySelector('[data-product-quantity]');
    const offerEnabled = root.dataset.offerEnabled === 'true';
    const offerQuantity = Number(root.dataset.offerQuantity || 2);
    const currentQuantity = Number(quantityInput?.value || 1);
    if (addButton) addButton.disabled = !variant.available;
    if (addText) {
      addText.textContent = variant.available
        ? (offerEnabled && currentQuantity >= offerQuantity
          ? `AJOUTER ${currentQuantity} AU PANIER — 1 OFFERT`
          : 'Ajouter au panier')
        : 'Épuisé';
    }

    if (history.replaceState) {
      const url = new URL(window.location.href);
      url.searchParams.set('variant', variant.id);
      history.replaceState({}, '', url.toString());
    }
  };

  const updateOfferButtonText = (quantityInput) => {
    const root = quantityInput.closest('[data-product-root]');
    if (!root) return;
    const addButton = root.querySelector('[data-add-to-cart]');
    const addText = root.querySelector('[data-add-to-cart-text]');
    if (!addButton || !addText || addButton.disabled) return;
    const offerEnabled = root.dataset.offerEnabled === 'true';
    const offerQuantity = Number(root.dataset.offerQuantity || 2);
    const quantity = Math.max(1, Number(quantityInput.value || 1));
    addText.textContent = offerEnabled && quantity >= offerQuantity
      ? `AJOUTER ${quantity} AU PANIER — 1 OFFERT`
      : 'Ajouter au panier';
  };

  document.addEventListener('change', (event) => {
    if (event.target.matches('[data-variant-select]')) updateVariantUI(event.target);
    if (event.target.matches('[data-product-quantity]')) updateOfferButtonText(event.target);
  });

  document.addEventListener('input', (event) => {
    if (event.target.matches('[data-product-quantity]')) updateOfferButtonText(event.target);
  });

  // Cart drawer.
  const drawer = document.querySelector('[data-cart-drawer]');
  const drawerOverlay = document.querySelector('[data-cart-overlay]');
  const drawerItems = document.querySelector('[data-cart-items]');
  const drawerFooter = document.querySelector('[data-cart-footer]');
  const drawerSubtotal = document.querySelector('[data-cart-subtotal]');
  let lastFocusedElement = null;

  const setDrawer = (open) => {
    if (!drawer || !drawerOverlay) return;
    drawer.classList.toggle('is-open', open);
    drawer.setAttribute('aria-hidden', String(!open));
    drawerOverlay.hidden = !open;
    body.classList.toggle('drawer-open', open);
    if (open) {
      lastFocusedElement = document.activeElement;
      drawer.querySelector('[data-cart-close]')?.focus();
    } else if (lastFocusedElement instanceof HTMLElement) {
      lastFocusedElement.focus();
    }
  };

  const renderCart = (cart) => {
    document.querySelectorAll('[data-cart-count]').forEach((element) => {
      element.textContent = cart.item_count;
      element.hidden = cart.item_count === 0;
    });

    if (!drawerItems || !drawerFooter) return;
    if (!cart.items.length) {
      drawerItems.innerHTML = '<p class="cart-drawer__empty">Ton panier est vide.</p>';
      drawerFooter.hidden = true;
      return;
    }

    drawerItems.innerHTML = cart.items.map((item) => {
      const image = item.image
        ? `<img src="${escapeHtml(item.image)}&width=180" alt="${escapeHtml(item.product_title)}" width="90" height="90" loading="lazy">`
        : '';
      const variant = item.variant_title && item.variant_title !== 'Default Title'
        ? `<p>${escapeHtml(item.variant_title)}</p>`
        : '';
      return `
        <article class="drawer-item" data-line-key="${escapeHtml(item.key)}">
          <a class="drawer-item__image" href="${escapeHtml(item.url)}">${image}</a>
          <div>
            <a href="${escapeHtml(item.url)}"><h3>${escapeHtml(item.product_title)}</h3></a>
            ${variant}
            <div class="drawer-item__bottom">
              <strong>${escapeHtml(formatMoney(item.final_line_price, cart.currency))}</strong>
              <div class="drawer-quantity" aria-label="Quantité">
                <button type="button" data-cart-decrease aria-label="Diminuer">−</button>
                <span>${item.quantity}</span>
                <button type="button" data-cart-increase aria-label="Augmenter">+</button>
              </div>
            </div>
            <button class="drawer-remove" type="button" data-cart-remove>Supprimer</button>
          </div>
        </article>`;
    }).join('');

    if (drawerSubtotal) drawerSubtotal.textContent = formatMoney(cart.total_price, cart.currency);
    drawerFooter.hidden = false;
  };

  const getCart = async () => {
    const response = await fetch(config.cartUrl, { headers: { Accept: 'application/json' } });
    if (!response.ok) throw new Error('Impossible de charger le panier.');
    const cart = await response.json();
    renderCart(cart);
    return cart;
  };

  const changeCartLine = async (key, quantity) => {
    const response = await fetch(config.cartChangeUrl, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ id: key, quantity })
    });
    if (!response.ok) throw new Error('Impossible de modifier le panier.');
    renderCart(await response.json());
  };

  document.querySelectorAll('[data-cart-open]').forEach((button) => button.addEventListener('click', async () => {
    try { await getCart(); } catch (error) { announce(error.message); }
    setDrawer(true);
  }));
  document.querySelector('[data-cart-close]')?.addEventListener('click', () => setDrawer(false));
  drawerOverlay?.addEventListener('click', () => setDrawer(false));

  drawerItems?.addEventListener('click', async (event) => {
    const line = event.target.closest('[data-line-key]');
    if (!line) return;
    const key = line.dataset.lineKey;
    const current = Number(line.querySelector('.drawer-quantity span')?.textContent || 1);
    let quantity = current;
    if (event.target.closest('[data-cart-increase]')) quantity = current + 1;
    else if (event.target.closest('[data-cart-decrease]')) quantity = Math.max(0, current - 1);
    else if (event.target.closest('[data-cart-remove]')) quantity = 0;
    else return;

    event.target.disabled = true;
    try { await changeCartLine(key, quantity); }
    catch (error) { announce(error.message); }
  });

  // AJAX add to cart.
  document.addEventListener('submit', async (event) => {
    const form = event.target.closest('[data-product-form]');
    if (!form) return;
    event.preventDefault();
    const button = form.querySelector('[data-add-to-cart]');
    const original = button?.querySelector('[data-add-to-cart-text]')?.textContent || '';
    if (button) button.disabled = true;
    const text = button?.querySelector('[data-add-to-cart-text]');
    if (text) text.textContent = 'Ajout…';

    try {
      const response = await fetch(config.cartAddUrl, {
        method: 'POST',
        headers: { Accept: 'application/json' },
        body: new FormData(form)
      });
      const result = await response.json();
      if (!response.ok) throw new Error(result.description || 'Impossible d’ajouter ce produit.');
      await getCart();
      setDrawer(true);
      announce('Produit ajouté au panier.');
    } catch (error) {
      announce(error.message);
      window.alert(error.message);
    } finally {
      if (button) button.disabled = false;
      if (text) text.textContent = original;
    }
  });

  document.addEventListener('keydown', (event) => {
    if (event.key !== 'Escape') return;
    if (drawer?.classList.contains('is-open')) setDrawer(false);
    if (mobileMenu?.classList.contains('is-open')) setMobileMenu(false);
  });
})();
